import {
	type UseMutationOptions,
	useMutation,
	useQueryClient
} from '@tanstack/react-query'
import {
	type HttpEditItemFlags,
	httpAddServiceItemFlags
} from '@/features/service-vetting/http/services-vetting.http.ts'
import type {
	ServiceFlag,
	ServiceGalleryImage
} from '@/features/service-vetting/types.ts'
import { errorHandler } from '@/helpers/utils'
import { QUERY_KEYS } from '@/query-client'

export const useAddServiceGalleryImageFlags = (
	urlParams: Omit<
		HttpEditItemFlags['urlParams'],
		'section' | 'item' | 'itemId'
	>,
	options?: Omit<
		UseMutationOptions<
			unknown,
			unknown,
			{ flags: ServiceFlag[]; itemId: number }
		>,
		'mutationFn'
	>
) => {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: (payload) =>
			httpAddServiceItemFlags({
				urlParams: {
					...urlParams,
					section: 'gallery',
					item: 'images',
					itemId: payload.itemId
				},
				payload: { flags: payload.flags.map((flag) => flag.id) },
				params: {
					response_type: 'small'
				}
			}),
		onError: (error, variables, onMutateResult, context) => {
			queryClient.setQueriesData(
				{
					queryKey: [
						QUERY_KEYS.SERVICE_GALLERY_IMAGES,
						urlParams.serviceId
					]
				},
				context
			)
			errorHandler(error)
			options?.onError?.(error, variables, onMutateResult, context)
		},
		onMutate: async (variables) => {
			const queryKey = [
				QUERY_KEYS.SERVICE_GALLERY_IMAGES,
				urlParams.serviceId
			]

			// Cancel any outgoing refetches (so they don't overwrite our optimistic update)
			await queryClient.cancelQueries({ queryKey })

			// Snapshot previous values
			const previousData =
				queryClient.getQueryData<ServiceGalleryImage[]>(queryKey)

			// Update cached data
			if (previousData) {
				queryClient.setQueriesData<ServiceGalleryImage[]>(
					{ queryKey },
					previousData.map((image) => {
						if (image.id === variables.itemId) {
							return {
								...image,
								flags: [...image.flags, ...variables.flags]
							}
						}
						return image
					})
				)
			}
			return previousData
		},
		...options
	})
}
